import { X } from 'lucide-react'
import { useState } from 'react'
import { IconField } from '@/components/admin/shared/icon-field'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { slugify } from '@/lib/slug'
import type { Source, SourceCategory, SourceFormData } from './types'
import { SOURCE_CATEGORIES } from './types'

interface AddSourceModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: SourceFormData) => Promise<Source>
}

export function AddSourceModal({
  isOpen,
  onClose,
  onSubmit,
}: AddSourceModalProps) {
  const [formData, setFormData] = useState<SourceFormData>({
    name: '',
    slug: '',
    icon: '',
    category: 'prep_resource',
    url: '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.name.trim()) {
      setError('Name is required')
      return
    }

    setIsSubmitting(true)
    setError(null)
    try {
      await onSubmit({
        name: formData.name.trim(),
        slug: formData.slug?.trim() || undefined,
        icon: formData.icon?.trim() || null,
        category: formData.category,
        url: formData.url?.trim() || null,
      })
      setFormData({
        name: '',
        slug: '',
        icon: '',
        category: 'prep_resource',
        url: '',
      })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create source')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    setFormData({
      name: '',
      slug: '',
      icon: '',
      category: 'prep_resource',
      url: '',
    })
    setError(null)
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="bg-background/80 fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
      <div className="bg-card border-border w-full max-w-md rounded-lg border p-6 shadow-lg">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-foreground text-lg font-semibold">Add Source</h2>
          <button
            type="button"
            onClick={handleClose}
            className="text-muted-foreground hover:text-foreground"
          >
            <X size={20} />
          </button>
        </div>

        {error && (
          <div className="bg-destructive/10 text-destructive mb-4 rounded-md p-3 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="source-name"
              className="text-muted-foreground mb-1 block text-sm font-medium"
            >
              Name <span className="text-destructive">*</span>
            </label>
            <Input
              id="source-name"
              type="text"
              value={formData.name}
              onChange={(e) => {
                setFormData({
                  ...formData,
                  name: e.target.value,
                  slug: formData.slug || slugify(e.target.value),
                })
              }}
              placeholder="e.g., Beyond Cracking the Coding Interview"
              required
            />
          </div>

          <div>
            <label
              htmlFor="source-slug"
              className="text-muted-foreground mb-1 block text-sm font-medium"
            >
              Slug
            </label>
            <Input
              id="source-slug"
              type="text"
              value={formData.slug}
              onChange={(e) =>
                setFormData({ ...formData, slug: e.target.value })
              }
              placeholder="e.g., beyond-ctci"
            />
            <p className="text-muted-foreground mt-1 text-xs">
              Leave empty to auto-generate from name
            </p>
          </div>

          <div>
            <label
              htmlFor="source-category"
              className="text-muted-foreground mb-1 block text-sm font-medium"
            >
              Category <span className="text-destructive">*</span>
            </label>
            <select
              id="source-category"
              value={formData.category}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  category: e.target.value as SourceCategory,
                })
              }
              className="border-input bg-background h-9 w-full rounded-md border px-3 text-sm"
            >
              {SOURCE_CATEGORIES.map((cat) => (
                <option key={cat.value} value={cat.value}>
                  {cat.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <IconField
              label="Icon"
              value={formData.icon ?? ''}
              onChange={(icon) => setFormData({ ...formData, icon })}
            />
          </div>

          <div>
            <label
              htmlFor="source-url"
              className="text-muted-foreground mb-1 block text-sm font-medium"
            >
              URL
            </label>
            <Input
              id="source-url"
              type="url"
              value={formData.url ?? ''}
              onChange={(e) =>
                setFormData({ ...formData, url: e.target.value })
              }
              placeholder="https://example.com"
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Adding...' : 'Add Source'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
